import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faNetworkWired, faTimes, faSave } from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { useTranslation } from 'react-i18next';

/**
 * IpForm
 *
 * Modal form for creating or editing a single IP address record. Performs
 * client-side validation for IPv4 format and description length.
 */
const IpForm = ({ ip, onSave, onCancel, loading = false }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    ip_address: '',
    description: '',
    is_active: true
  });
  const strings = t('ipAddresses.forms.single', { returnObjects: true });
  const requiredIndicator = t('common.form.requiredIndicator');
  const [errors, setErrors] = useState({});
  const isEditing = Boolean(ip);
  const title = isEditing ? strings.title.edit : strings.title.add;
  const subtitle = isEditing ? strings.subtitle.edit : strings.subtitle.add;
  const submitLabel = isEditing ? strings.buttons.submit.edit : strings.buttons.submit.add;
  const submitLoadingLabel = isEditing ? strings.buttons.submit.loading.edit : strings.buttons.submit.loading.add;

  // Initialize form values when the `ip` prop changes (edit mode) or reset
  // to defaults when creating a new IP.
  useEffect(() => {
    if (ip) {
      setFormData({
        ip_address: ip.ip_address || '',
        description: ip.description || '',
        is_active: ip.is_active !== undefined ? ip.is_active : true
      });
    } else {
      setFormData({
        ip_address: '',
        description: '',
        is_active: true
      });
    }
  }, [ip]);

  /**
   * validateForm()
   *
   * Validates the IP address format and description field. Returns true
   * when the form is valid and populates `errors` for invalid fields.
   */
  const validateForm = () => {
    const newErrors = {};

    // IP Address validation
    if (!formData.ip_address.trim()) {
      newErrors.ip_address = strings.fields.ipAddress.errors.required;
    } else {
      // Basic IP address format validation
      const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
      if (!ipRegex.test(formData.ip_address.trim())) {
        newErrors.ip_address = strings.fields.ipAddress.errors.invalid;
      }
    }

    // Description validation
    if (!formData.description.trim()) {
      newErrors.description = strings.fields.description.errors.required;
    } else if (formData.description.trim().length < 5) {
      newErrors.description = strings.fields.description.errors.minLength;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * handleInputChange(e)
   *
   * Generic input handler that supports text and checkbox inputs and
   * clears field-level errors as the user edits.
   */
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  /**
   * handleSubmit(e)
   *
   * Validate and call `onSave` with a trimmed submit payload.
   */
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const submitData = {
      ip_address: formData.ip_address.trim(),
      description: formData.description.trim(),
      is_active: formData.is_active
    };

    onSave(submitData);
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}
    >
      <style>{`.ip-form-modal{ -ms-overflow-style: none; scrollbar-width: none; } .ip-form-modal::-webkit-scrollbar{ display: none; }`}</style>
      <div 
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto ip-form-modal"
        style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}
      >
        {/* Header */}
        <div 
          className="flex items-center justify-between p-6 border-b"
          style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
        >
          <div className="flex items-center space-x-4">
            <div 
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ 
                backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background,
                color: UI_COLORS.BUTTONS.PRIMARY.text 
              }}
            >
              <FontAwesomeIcon icon={faNetworkWired} className="text-lg" />
            </div>
            <div>
              <h2 
                className="text-2xl font-bold"
                style={{ color: UI_COLORS.TEXT.PRIMARY }}
              >
                {title}
              </h2>
              <p className="mt-1 text-base" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                {subtitle}
              </p>
            </div>
          </div>

          <button
            onClick={onCancel}
            className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
            style={{ 
              color: UI_COLORS.TEXT.MUTED,
              backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1)
            }}
            onMouseEnter={(e) => { e.target.style.backgroundColor = colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.2); }}
            onMouseLeave={(e) => { e.target.style.backgroundColor = colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1); }}
          >
            <FontAwesomeIcon icon={faTimes} className="text-lg" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* IP Address Field */}
          <div>
            <label htmlFor="ip_address" className="block text-sm font-semibold mb-2" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
              {strings.fields.ipAddress.label} <span style={{ color: UI_COLORS.STATUS.ERROR }}>{requiredIndicator}</span>
            </label>
            <input
              type="text"
              id="ip_address"
              name="ip_address"
              value={formData.ip_address}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 border-2 rounded-xl transition-all duration-200 focus:ring-2 ${
                errors.ip_address ? 'border-red-300 focus:border-red-500' : 'focus:border-transparent'
              }`}
              style={{
                backgroundColor: UI_COLORS.FORMS.INPUT.background,
                borderColor: errors.ip_address ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border,
                color: UI_COLORS.FORMS.INPUT.text,
                '--tw-ring-color': colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.3)
              }}
              placeholder={strings.fields.ipAddress.placeholder}
              disabled={loading}
            />
            {errors.ip_address && (
              <p className="mt-2 text-sm font-medium" style={{ color: UI_COLORS.STATUS.ERROR }}>
                {errors.ip_address}
              </p>
            )}
          </div>

          {/* Description Field */}
          <div>
            <label htmlFor="description" className="block text-sm font-semibold mb-2" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
              {strings.fields.description.label} <span style={{ color: UI_COLORS.STATUS.ERROR }}>{requiredIndicator}</span>
            </label>
            <input
              type="text"
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 border-2 rounded-xl transition-all duration-200 focus:ring-2 ${
                errors.description ? 'border-red-300 focus:border-red-500' : 'focus:border-transparent'
              }`}
              style={{
                backgroundColor: UI_COLORS.FORMS.INPUT.background,
                borderColor: errors.description ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border,
                color: UI_COLORS.FORMS.INPUT.text,
                '--tw-ring-color': colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.3)
              }}
              placeholder={strings.fields.description.placeholder}
              disabled={loading}
            />
            {errors.description && (
              <p className="mt-2 text-sm font-medium" style={{ color: UI_COLORS.STATUS.ERROR }}>
                {errors.description}
              </p>
            )}
          </div>

          {/* Active Status Field */}
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="is_active"
              name="is_active"
              checked={formData.is_active}
              onChange={handleInputChange}
              className="h-5 w-5 rounded border-2 focus:ring-2 transition-all duration-200"
              style={{ 
                accentColor: UI_COLORS.BUTTONS.PRIMARY.background,
                borderColor: UI_COLORS.FORMS.INPUT.border
              }}
              disabled={loading}
            />
            <label htmlFor="is_active" className="text-base font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
              {strings.fields.isActive.label}
            </label>
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end space-x-4 pt-6">
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="px-6 py-3 rounded-xl font-semibold transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:transform-none"
              style={{
                backgroundColor: 'transparent',
                color: UI_COLORS.TEXT.MUTED,
                border: `2px solid ${UI_COLORS.BORDERS.MEDIUM}`
              }}
              onMouseEnter={(e) => { if (!loading) { e.target.style.backgroundColor = colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1); } }}
              onMouseLeave={(e) => { if (!loading) { e.target.style.backgroundColor = 'transparent'; } }}
            >
              {strings.buttons.cancel}
            </button>

            <button
              type="submit"
              className="px-6 py-3 rounded-xl font-semibold transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:transform-none"
              style={{
                backgroundColor: loading 
                  ? UI_COLORS.BUTTONS.PRIMARY.loading.background 
                  : UI_COLORS.BUTTONS.PRIMARY.background,
                color: loading 
                  ? UI_COLORS.BUTTONS.PRIMARY.loading.text 
                  : UI_COLORS.BUTTONS.PRIMARY.text
              }}
            >
              {loading ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>{submitLoadingLabel}</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <FontAwesomeIcon icon={faSave} />
                  <span>{submitLabel}</span>
                </div>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default IpForm;